// Session PJ2: the field controls of the engineer's survey sheet. One control per field kind in the template
// (src/lib/surveyTemplate.ts), each reading a value from the merged record and reporting a change as a NEW CELL stamped
// with the phone's clock at the moment of the change. Large targets (44px and up), the numeric keypad for measurements with
// the unit beside the box, and no native date or time picker anywhere.
import { useEffect, useId, useState } from "react";
import {
  NO_ACCESS_REASONS,
  NO_ACCESS_REASON_CODES,
  type NoAccessReason,
  type SurveyCell,
  type SurveyRecordData,
  type SurveyValue,
  cellPath,
  fieldPath,
  rowPath,
  valueAt,
} from "../../../lib/surveyRecord";
import type { ChipOption, SurveyField, TableColumn } from "../../../lib/surveyTemplate";
import { noAccessCell, readOtherWords, tableRows, valueCell, writeOtherWords } from "../../../lib/projectSurveyDraft";

export type SetCell = (path: string, cell: SurveyCell) => void;

const chipCls = (active: boolean) =>
  `min-h-12 rounded-xl border px-3 py-2 text-left font-menu text-sm transition-colors ${
    active ? "border-ele-green bg-ele-green text-ele-bg" : "border-ele-card-border bg-ele-field text-ele-strong"
  }`;
const inputCls =
  "min-h-12 w-full min-w-0 rounded-xl border border-ele-field-border bg-ele-field px-3 py-2 text-base text-ele-strong outline-none focus:border-ele-field-focus";
const quietBtn = "min-h-11 rounded-xl border border-ele-card-border px-3 py-2 font-menu text-sm text-ele-body";

// ---- Chips ------------------------------------------------------------------------------------------------------------
export function Chips({
  label,
  options,
  multi,
  value,
  onChange,
}: {
  label: string;
  options: readonly ChipOption[];
  multi: boolean;
  value: SurveyValue | undefined;
  onChange: (v: SurveyValue | null) => void;
}) {
  const id = useId();
  const selected = Array.isArray(value) ? value : typeof value === "string" ? [value] : [];
  const tap = (opt: string) => {
    if (multi) {
      const next = selected.includes(opt) ? selected.filter((x) => x !== opt) : [...selected, opt];
      onChange(next.length ? next : null);
    } else {
      onChange(selected[0] === opt ? null : opt);
    }
  };
  return (
    <div role="group" aria-labelledby={id}>
      <p id={id} className="sr-only">{label}</p>
      <div className="grid grid-cols-2 gap-2">
        {options.map((o) => (
          <button key={o.value} type="button" aria-pressed={selected.includes(o.value)} className={chipCls(selected.includes(o.value))} onClick={() => tap(o.value)}>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

const YES_NO: ChipOption[] = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
];
const YES_NO_UNKNOWN: ChipOption[] = [...YES_NO, { value: "unknown", label: "Not known" }];

// ---- A number box (mm or a plain number) --------------------------------------------------------------------------------
// The text in the box is kept while it is being typed ("6." on the way to "6.5"); a whole number is committed as a number,
// an empty box clears the value, and anything else is left in the box and not saved.
export function NumberBox({
  label,
  unit,
  decimal,
  value,
  onChange,
  hideLabel,
}: {
  label: string;
  unit?: string;
  decimal: boolean;
  value: SurveyValue | undefined;
  onChange: (v: number | null) => void;
  hideLabel?: boolean;
}) {
  const id = useId();
  const shown = typeof value === "number" ? String(value) : typeof value === "string" ? value : "";
  const [text, setText] = useState(shown);
  useEffect(() => {
    setText((cur) => (Number(cur) === Number(shown) && cur !== "" && shown !== "" ? cur : shown));
  }, [shown]);
  const re = decimal ? /^\d{0,6}(?:\.\d{0,2})?$/ : /^\d{0,6}$/;
  return (
    <div>
      <label htmlFor={id} className={hideLabel ? "sr-only" : "mb-1 block text-sm text-ele-body"}>
        {label}
      </label>
      <div className="flex items-center gap-2">
        <input
          id={id}
          type="text"
          inputMode={decimal ? "decimal" : "numeric"}
          autoComplete="off"
          enterKeyHint="next"
          className={`${inputCls} text-right font-heading text-lg`}
          value={text}
          onChange={(e) => {
            const raw = e.target.value.replace(",", ".").trim();
            if (!re.test(raw)) return;
            setText(raw);
            if (raw === "") onChange(null);
            else if (/^\d+(?:\.\d+)?$/.test(raw)) onChange(Number(raw));
          }}
        />
        {unit && <span className="w-14 shrink-0 font-menu text-sm text-ele-muted">{unit}</span>}
      </div>
    </div>
  );
}

// ---- Text, with dictation (the phone's own microphone key does it) ------------------------------------------------------
export function TextBox({
  label,
  value,
  onChange,
  hint,
  multiline,
  hideLabel,
}: {
  label: string;
  value: SurveyValue | undefined;
  onChange: (v: string | null) => void;
  hint?: string;
  multiline?: boolean;
  hideLabel?: boolean;
}) {
  const id = useId();
  const v = typeof value === "string" ? value : "";
  return (
    <div>
      <label htmlFor={id} className={hideLabel ? "sr-only" : "mb-1 block text-sm text-ele-body"}>
        {label}
      </label>
      {multiline ? (
        <textarea id={id} rows={3} className={inputCls} value={v} placeholder={hint} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)} />
      ) : (
        <input id={id} type="text" autoComplete="off" className={inputCls} value={v} placeholder={hint} onChange={(e) => onChange(e.target.value === "" ? null : e.target.value)} />
      )}
    </div>
  );
}

// ---- Date and time on site: a Now button and the time typed as four digits (never a native picker with a clock dial) --
function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return "";
  return d.toLocaleString("en-GB", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

export function DateTimeField({ label, value, onChange }: { label: string; value: SurveyValue | undefined; onChange: (v: string | null) => void }) {
  const id = useId();
  const iso = typeof value === "string" ? value : "";
  const [hhmm, setHhmm] = useState("");
  const setTime = (raw: string) => {
    const digits = raw.replace(/\D/g, "").slice(0, 4);
    setHhmm(digits);
    if (digits.length === 4) {
      const h = Number(digits.slice(0, 2));
      const m = Number(digits.slice(2));
      if (h < 24 && m < 60) {
        const base = iso ? new Date(iso) : new Date();
        base.setHours(h, m, 0, 0);
        onChange(base.toISOString());
      }
    }
  };
  return (
    <div>
      <p className="mb-1 text-sm text-ele-body">{label}</p>
      <p className="mb-2 font-heading text-lg text-ele-strong" aria-live="polite">{iso ? formatWhen(iso) : "Not set"}</p>
      <div className="flex items-center gap-2">
        <button type="button" className="min-h-12 shrink-0 rounded-xl bg-ele-green px-5 font-heading text-lg text-ele-bg" onClick={() => onChange(new Date().toISOString())}>
          Now
        </button>
        <label htmlFor={id} className="sr-only">{label} time</label>
        <input id={id} type="text" inputMode="numeric" placeholder="HHMM" maxLength={4} className={`${inputCls} text-center`} value={hhmm} onChange={(e) => setTime(e.target.value)} />
        {iso && (
          <button type="button" className={quietBtn} onClick={() => { setHhmm(""); onChange(null); }}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

// ---- Could not access: a reason from the fixed list, a few words for Other ----------------------------------------------
export function NoAccessControl({
  what,
  cell,
  onMark,
  onClear,
}: {
  what: string;
  cell: SurveyCell | undefined;
  onMark: (reason: NoAccessReason, note?: string) => void;
  onClear: () => void;
}) {
  const [open, setOpen] = useState(false);
  const na = cell?.na;
  if (na) {
    return (
      <div className="mt-2 rounded-xl border border-ele-card-border p-2" style={{ backgroundColor: "var(--ele-pill-tint-amber)" }}>
        <p className="text-sm text-ele-strong">
          Could not access: {NO_ACCESS_REASONS[na.reason]}
          {na.note ? `, ${na.note}` : ""}
        </p>
        {na.reason === "other" && (
          <input
            aria-label={`Why ${what} could not be accessed`}
            className={`${inputCls} mt-2`}
            placeholder="A few words"
            maxLength={300}
            value={na.note ?? ""}
            onChange={(e) => onMark("other", e.target.value)}
          />
        )}
        <button type="button" className={`${quietBtn} mt-2`} onClick={onClear}>
          I can access it now
        </button>
      </div>
    );
  }
  if (!open) {
    return (
      <button type="button" className="mt-1 min-h-11 font-menu text-xs text-ele-muted underline" onClick={() => setOpen(true)}>
        Could not access
      </button>
    );
  }
  return (
    <div className="mt-2" role="group" aria-label={`Why ${what} could not be accessed`}>
      <div className="grid grid-cols-2 gap-2">
        {NO_ACCESS_REASON_CODES.map((r) => (
          <button key={r} type="button" className={chipCls(false)} onClick={() => { setOpen(false); onMark(r); }}>
            {NO_ACCESS_REASONS[r]}
          </button>
        ))}
      </div>
      <button type="button" className={`${quietBtn} mt-2`} onClick={() => setOpen(false)}>
        Cancel
      </button>
    </div>
  );
}

// ---- One data field (not a table, not a photo) --------------------------------------------------------------------------
export function FieldControl({
  field,
  record,
  setCell,
  notesKey,
}: {
  field: SurveyField;
  record: SurveyRecordData;
  setCell: SetCell;
  /** The notes path of the field's section: where an "Other" chip's words are kept (projectSurveyDraft.ts). */
  notesKey: string;
}) {
  const path = fieldPath(field.key);
  const cell = record.cells[path];
  const value = valueAt(record, path);
  const now = () => new Date();
  const set = (v: SurveyValue | null) => setCell(path, valueCell(v, now()));
  const label = field.unit && field.kind !== "mm" && field.kind !== "number" ? `${field.label} (${field.unit})` : field.label;
  const notesValue = valueAt(record, notesKey);
  const notes = typeof notesValue === "string" ? notesValue : "";
  const otherChosen = Array.isArray(value) ? value.includes("other") : value === "other";

  let control: React.ReactNode = null;
  if (!cell?.na) {
    switch (field.kind) {
      case "chips_single":
      case "chips_multi":
        control = (
          <>
            <Chips label={field.label} options={field.options ?? []} multi={field.kind === "chips_multi"} value={value} onChange={set} />
            {field.allowOther && otherChosen && (
              <div className="mt-2">
                <TextBox
                  label={`${field.label}: other`}
                  hint="Say what it is"
                  value={readOtherWords(notes, field)}
                  onChange={(w) => setCell(notesKey, valueCell(writeOtherWords(notes, field, w ?? "") || null, now()))}
                />
              </div>
            )}
          </>
        );
        break;
      case "yes_no":
        control = <Chips label={field.label} options={YES_NO} multi={false} value={value} onChange={set} />;
        break;
      case "yes_no_unknown":
        control = <Chips label={field.label} options={YES_NO_UNKNOWN} multi={false} value={value} onChange={set} />;
        break;
      case "mm":
        control = <NumberBox label={field.label} hideLabel unit="mm" decimal={false} value={value} onChange={set} />;
        break;
      case "number":
        control = (
          <NumberBox
            label={field.label}
            hideLabel
            unit={field.unit}
            // Most counts are whole numbers; a measured figure with a unit (rope diameter 6.5 mm, 0.63 m/s) may need a point.
            decimal={field.key !== "floors_count" && !!field.unit}
            value={value}
            onChange={set}
          />
        );
        break;
      case "text":
        control = <TextBox label={field.label} hideLabel hint={field.hint ?? "You can dictate"} value={value} onChange={set} />;
        break;
      case "datetime":
        control = <DateTimeField label={field.label} value={value} onChange={set} />;
        break;
      default:
        control = null;
    }
  }

  return (
    <div className="mb-4 rounded-xl border border-ele-card-border bg-ele-card p-3" data-field={field.key}>
      {field.kind !== "datetime" && (
        <p className="mb-2 font-heading text-base text-ele-strong">
          {label}
          {field.required && <span className="ml-1 text-ele-muted">(needed)</span>}
        </p>
      )}
      {field.hint && field.kind !== "text" && <p className="mb-2 text-xs text-ele-muted">{field.hint}</p>}
      {control}
      {!field.required && (
        <NoAccessControl
          what={field.label}
          cell={cell}
          onMark={(reason, note) => setCell(path, noAccessCell(reason, note, now()))}
          onClear={() => setCell(path, valueCell(null, now()))}
        />
      )}
    </div>
  );
}

// ---- A table: one card per floor (or level), so it fits a phone with no sideways scroll -------------------------------
function ColumnControl({
  column,
  field,
  row,
  rowLabel,
  record,
  setCell,
}: {
  column: TableColumn;
  field: SurveyField;
  row: string;
  rowLabel: string;
  record: SurveyRecordData;
  setCell: SetCell;
}) {
  const path = cellPath(field.key, row, column.key);
  const value = valueAt(record, path);
  const set = (v: SurveyValue | null) => setCell(path, valueCell(v, new Date()));
  const a11y = `${column.label}, ${rowLabel}`;
  if (column.kind === "mm" || column.kind === "number") {
    return <NumberBox label={a11y} hideLabel unit={column.unit ?? (column.kind === "mm" ? "mm" : undefined)} decimal={column.kind === "number"} value={value} onChange={set} />;
  }
  if (column.kind === "chips_single" || column.kind === "chips_multi") {
    return <Chips label={a11y} options={column.options ?? []} multi={column.kind === "chips_multi"} value={value} onChange={set} />;
  }
  return <TextBox label={a11y} hideLabel hint={column.hint} value={value} onChange={set} />;
}

export function TableField({ field, record, setCell }: { field: SurveyField; record: SurveyRecordData; setCell: SetCell }) {
  const rows = tableRows(field, record);
  const columns = field.columns ?? [];
  if (rows.length === 0) {
    return (
      <div className="mb-4 rounded-xl border border-ele-card-border bg-ele-card p-3" data-field={field.key}>
        <p className="font-heading text-base text-ele-strong">{field.label}</p>
        <p className="mt-1 text-sm text-ele-muted">Enter the number of floors first (Floors and travel). One row per floor appears here.</p>
      </div>
    );
  }
  const sameAsAbove = (i: number) => {
    const above = rows[i - 1]!.key;
    const here = rows[i]!.key;
    const at = new Date();
    for (const c of columns) {
      if (c.isRowName) continue; // a floor keeps its own name
      const v = valueAt(record, cellPath(field.key, above, c.key));
      if (v !== undefined) setCell(cellPath(field.key, here, c.key), valueCell(v, at));
    }
  };
  return (
    <div className="mb-4" data-field={field.key}>
      <p className="mb-2 font-heading text-base text-ele-strong">{field.label}</p>
      {field.hint && <p className="mb-2 text-xs text-ele-muted">{field.hint}</p>}
      <ol className="space-y-3">
        {rows.map((r, i) => {
          const rp = rowPath(field.key, r.key);
          const rowCell = record.cells[rp];
          return (
            <li key={r.key} className="rounded-xl border border-ele-card-border bg-ele-card p-3" aria-label={r.label}>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <p className="font-heading text-base text-ele-strong">{r.label}</p>
                {i > 0 && !rowCell?.na && (
                  <button type="button" className={quietBtn} onClick={() => sameAsAbove(i)} aria-label={`Same as above for ${r.label}`}>
                    Same as above
                  </button>
                )}
              </div>
              {!rowCell?.na && (
                <div className="grid grid-cols-1 gap-3">
                  {columns.map((c) => (
                    <div key={c.key}>
                      <p className="mb-1 text-xs text-ele-muted">{c.label}</p>
                      <ColumnControl column={c} field={field} row={r.key} rowLabel={r.label} record={record} setCell={setCell} />
                    </div>
                  ))}
                </div>
              )}
              <NoAccessControl
                what={r.label}
                cell={rowCell}
                onMark={(reason, note) => setCell(rp, noAccessCell(reason, note, new Date()))}
                onClear={() => setCell(rp, valueCell(null, new Date()))}
              />
            </li>
          );
        })}
      </ol>
    </div>
  );
}
